import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useId } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { companyApi } from '@/api';
import {
  Button,
  Card,
  CardHeader,
  Field,
  IconButton,
  Input,
  Textarea,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import {
  ACCENT_TOO_LIGHT,
  ACCENT_VAR,
  accentStyle,
  contrastWithWhite,
  HEX_COLOR,
  MINIMUM_ACCENT_CONTRAST,
} from '@/utils/branding';
import { cn } from '@/utils/cn';
import { companyBrandingSchema, MAX_BRAND_VALUES } from '@/validations';

import { applyBrandingFieldErrors, useApplyBranding } from './cache';
import { toFormValues } from './formValues';

/** What the colour picker shows while no accent is set: the brand violet. */
const PICKER_PLACEHOLDER = '#6d28d9';

/**
 * Everything on the employer page that is text: tagline, accent, mission,
 * culture, values, perks and the video link. Photos have their own editors,
 * because each upload is saved the moment it lands.
 *
 * `readOnly` disables the whole form through one `<fieldset disabled>`, so a
 * recruiter sees exactly what a student will without a control that looks
 * usable and is not.
 */
export function BrandingForm({ branding, perkCatalogue = [], companyName, readOnly = false }) {
  const toast = useToast();
  const applyBranding = useApplyBranding();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    resolver: zodResolver(companyBrandingSchema),
    defaultValues: toFormValues(branding),
  });

  const values = useFieldArray({ control, name: 'values' });

  const save = useMutation({ mutationFn: (payload) => companyApi.updateBranding(payload) });

  async function onSubmit(payload) {
    try {
      const updated = await save.mutateAsync(payload);
      applyBranding(updated);
      // Re-seed from what the API stored (it trims and lower-cases), which
      // also clears the dirty state.
      reset(toFormValues(updated));
      toast.success('Employer page saved');
    } catch (error) {
      if (!applyBrandingFieldErrors(error, setError)) {
        toast.error(errorMessage(error, 'Could not save the page'));
      }
    }
  }

  const tagline = watch('tagline');
  const accent = watch('accent_color');
  const selectedPerks = watch('perks');

  function togglePerk(code) {
    const next = selectedPerks.includes(code)
      ? selectedPerks.filter((item) => item !== code)
      : [...selectedPerks, code];
    setValue('perks', next, { shouldDirty: true });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <fieldset disabled={readOnly} className="min-w-0 space-y-6">
        <Card>
          <CardHeader
            title="Headline"
            description="The first things a student sees on your company page."
          />
          <div className="space-y-4">
            <Input
              label="Tagline"
              placeholder="We build the tools small shops run on"
              hint="One line, up to 160 characters."
              error={errors.tagline?.message}
              {...register('tagline')}
            />
            <AccentField
              value={accent}
              error={errors.accent_color?.message}
              register={register('accent_color')}
              onPick={(hex) =>
                setValue('accent_color', hex, { shouldDirty: true, shouldValidate: true })
              }
              onClear={() =>
                setValue('accent_color', '', { shouldDirty: true, shouldValidate: true })
              }
            />
            <HeadlinePreview name={companyName} tagline={tagline} accent={accent} />
          </div>
        </Card>

        <Card>
          <CardHeader title="Story" description="Plain text; line breaks are kept." />
          <div className="space-y-4">
            <Textarea
              label="Mission"
              rows={3}
              placeholder="Why your company exists."
              error={errors.mission?.message}
              {...register('mission')}
            />
            <Textarea
              label="Life as an intern"
              rows={6}
              placeholder="What a typical week looks like: who they work with, what they ship."
              hint="Up to 4000 characters."
              error={errors.culture?.message}
              {...register('culture')}
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Values"
            description={`How your team works, in up to ${MAX_BRAND_VALUES} short statements.`}
            action={
              !readOnly && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={values.fields.length >= MAX_BRAND_VALUES}
                  onClick={() => values.append({ title: '', description: '' })}
                >
                  Add value
                </Button>
              )
            }
          />
          {values.fields.length === 0 ? (
            <p className="text-ink-muted text-sm">No values yet.</p>
          ) : (
            <ol className="space-y-4">
              {values.fields.map((field, index) => (
                <li
                  key={field.id}
                  className="border-line rounded-input grid gap-3 border p-3 sm:grid-cols-[1fr_auto]"
                >
                  <div className="space-y-3">
                    <Input
                      label={`Value ${index + 1}`}
                      placeholder="Ship small, ship often"
                      error={errors.values?.[index]?.title?.message}
                      {...register(`values.${index}.title`)}
                    />
                    <Textarea
                      label="What it means in practice"
                      rows={2}
                      error={errors.values?.[index]?.description?.message}
                      {...register(`values.${index}.description`)}
                    />
                  </div>
                  {!readOnly && (
                    <IconButton
                      icon="trash"
                      label={`Remove value ${index + 1}`}
                      variant="ghostDanger"
                      onClick={() => values.remove(index)}
                    />
                  )}
                </li>
              ))}
            </ol>
          )}
          {errors.values?.message && (
            <p className="text-caption text-danger-600 mt-2" role="alert">
              {errors.values.message}
            </p>
          )}
        </Card>

        <Card>
          <CardHeader title="Perks" description="What interns get. Pick all that apply." />
          <div className="flex flex-wrap gap-2" role="group" aria-label="Perks">
            {perkCatalogue.map((perk) => {
              const selected = selectedPerks.includes(perk.code);
              return (
                <button
                  key={perk.code}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => togglePerk(perk.code)}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm font-medium ring-1 transition ring-inset',
                    'disabled:cursor-not-allowed',
                    selected
                      ? 'bg-brand-600 text-white ring-brand-600'
                      : 'bg-surface text-ink-muted ring-line hover:ring-brand-300 hover:text-ink',
                  )}
                >
                  {perk.label}
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          <CardHeader title="Video" description="A YouTube or Vimeo link to a single video." />
          <Input
            label="Video link"
            placeholder="https://www.youtube.com/watch?v=…"
            error={errors.video_url?.message}
            {...register('video_url')}
          />
        </Card>

        {!readOnly && (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!isDirty}
              onClick={() => reset(toFormValues(branding))}
            >
              Discard changes
            </Button>
            <Button type="submit" isLoading={isSubmitting || save.isPending} disabled={!isDirty}>
              Save changes
            </Button>
          </div>
        )}
      </fieldset>
    </form>
  );
}

/**
 * Colour picker and hex field bound to the same value, with the contrast
 * against white worked out as the user types -- the same WCAG sum and 4.5
 * threshold the API applies on save.
 */
function AccentField({ value, error, register, onPick, onClear }) {
  const pickerId = useId();
  const hex = (value ?? '').trim();
  const valid = HEX_COLOR.test(hex);
  const ratio = valid ? contrastWithWhite(hex) : null;

  const tooLight = ratio !== null && ratio < MINIMUM_ACCENT_CONTRAST;

  let hint = 'Buttons and the cover band use this, always with white text on top.';
  if (ratio !== null) hint = `Contrast with white text ${ratio.toFixed(1)}:1 — readable.`;
  // Said as the user types, not only on save: the same sum the API runs.
  const shown =
    error ??
    (tooLight
      ? `${ACCENT_TOO_LIGHT} (${ratio.toFixed(1)}:1; needs ${MINIMUM_ACCENT_CONTRAST}:1)`
      : undefined);

  return (
    <Field label="Accent colour" htmlFor={pickerId} error={shown} hint={hint}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={pickerId}
          type="color"
          value={valid ? hex.toLowerCase() : PICKER_PLACEHOLDER}
          onChange={(event) => onPick(event.target.value)}
          className="border-line rounded-input h-10 w-12 cursor-pointer border bg-transparent p-1 disabled:cursor-not-allowed"
          aria-label="Pick an accent colour"
        />
        <input
          type="text"
          inputMode="text"
          spellCheck={false}
          placeholder="#1d4ed8"
          maxLength={7}
          aria-label="Accent colour hex code"
          aria-invalid={Boolean(shown) || undefined}
          className={cn(
            'border-line bg-surface text-ink rounded-input h-10 w-32 border px-3 font-mono text-sm',
            'focus:border-brand-500 focus:ring-brand-100 focus:ring-4 focus:outline-none',
            shown && 'border-danger-500',
          )}
          {...register}
        />
        {hex && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            Use the default
          </Button>
        )}
      </div>
    </Field>
  );
}

/** What the cover band and apply button will look like, before saving. */
function HeadlinePreview({ name, tagline, accent }) {
  return (
    <div aria-label="Preview" className="rounded-input overflow-hidden border border-line">
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 text-white"
        style={{ ...accentStyle(accent), background: ACCENT_VAR }}
      >
        <div className="min-w-0">
          <p className="truncate font-semibold">{name || 'Your company'}</p>
          <p className="truncate text-sm text-white/85">{tagline || 'Your tagline appears here'}</p>
        </div>
        <span className="rounded-input bg-white/15 px-3 py-1.5 text-sm font-medium ring-1 ring-white/40 ring-inset">
          View open roles
        </span>
      </div>
      <p className="text-ink-muted bg-surface-muted/60 px-4 py-2 text-xs">
        Preview — an unreadable colour falls back to the Wonder violet.
      </p>
    </div>
  );
}
